/**
 * Shared allocation helpers for the Investment Rebalancing control (wizard + results).
 */

interface HoldingLike {
  assetClass?: string;
  currentValue?: number;
}

/**
 * The blended equity fraction (0–1) implied by a plan's holdings, mirroring the
 * engine's initialization maths: EQUITY counts as equity, BOND as bond, MIXED
 * splits 60/40 equity/bond, and OTHER/cash contribute to neither (out of both
 * numerator and denominator). Returns null when there's no equity-or-bond value
 * to divide by, so callers can fall back to a default.
 *
 * `holdings` is the wizard's per-person map: { [personIndex]: HoldingData[] }.
 */
export function blendedEquityPctFromHoldings(
  holdings: Record<string, HoldingLike[]> | undefined | null,
): number | null {
  if (!holdings) return null;
  let equity = 0;
  let bond = 0;
  for (const list of Object.values(holdings)) {
    if (!Array.isArray(list)) continue;
    for (const h of list) {
      const v = h?.currentValue ?? 0;
      if (v <= 0) continue;
      if (h.assetClass === 'EQUITY') equity += v;
      else if (h.assetClass === 'BOND') bond += v;
      else if (h.assetClass === 'MIXED') { equity += v * 0.6; bond += v * 0.4; }
      // OTHER and cash contribute to neither, matching the engine.
    }
  }
  const total = equity + bond;
  return total > 0 ? equity / total : null;
}
